"use client"

import { API_PREFIX_CAREER_FORMS_PATH } from "@/constants/prefix-path-api"
import { message } from "@/lib/config/lib/antd-global"
import { AxiosRequestConfig } from "axios"
import { useCallback, useState } from "react"
import { AdminHttpClient } from "../../../../app/api/AdminHttpClient"
import { t } from "../../../../languages/i18n"

const careerFormService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const getCareerForms = async (params?: AxiosRequestConfig["params"]) => {
    setIsLoading(true)
    try {
      const response = await AdminHttpClient.get(API_PREFIX_CAREER_FORMS_PATH, {
        params,
      })
      return response
    } catch (error) {
      message.error(t("Lỗi khi tải danh sách đơn tuyển dụng."))
      console.error("GET_CAREER_FORMS_ERROR:", error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const getCareerFormById = useCallback(async (id: number | string) => {
    setIsLoading(true)
    if (!id) {
      message.error(t("Id đơn tuyển dụng không hợp lệ."))
      setIsLoading(false)
      return null
    }
    const params: AxiosRequestConfig["params"] = {
      populate: "*",
    }
    try {
      const response = await AdminHttpClient.get(`${API_PREFIX_CAREER_FORMS_PATH}/${id}`, { params })
      return response?.data || response
    } catch (error) {
      message.error(t("Lỗi khi tải chi tiết đơn tuyển dụng."))
      console.error("GET_CAREER_FORM_BY_ID_ERROR:", error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createCareerForm = async (payload: {
    data: {
      fullname: string
      phone: string
      email: string
      selfInstruction?: string
      cv?: (string | number)[]
      resolved?: boolean
      domain?: string
      career?: string | number
      publishedAt?: string | null
    }
  }) => {
    setIsLoading(true)
    try {
      const response = await AdminHttpClient.post(API_PREFIX_CAREER_FORMS_PATH, payload)
      return response
    } catch (error) {
      message.error(t("Lỗi khi tạo đơn tuyển dụng."))
      console.error("CREATE_CAREER_FORM_ERROR:", error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateCareerForm = async (
    id: number | string,
    payload: {
      data: {
        fullname?: string
        phone?: string
        email?: string
        selfInstruction?: string
        cv?: (string | number)[]
        resolved?: boolean
        domain?: string
        career?: string | number
        publishedAt?: string | null
      }
    },
  ) => {
    setIsLoading(true)
    if (!id) {
      message.error(t("Thiếu id đơn tuyển dụng để cập nhật."))
      setIsLoading(false)
      return null
    }
    try {
      const response = await AdminHttpClient.put(`${API_PREFIX_CAREER_FORMS_PATH}/${id}`, payload)
      message.success(t("Cập nhật đơn tuyển dụng thành công!"))
      return response
    } catch (error) {
      message.error(t("Lỗi khi cập nhật đơn tuyển dụng."))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteCareerForm = async (id: number | string) => {
    setIsLoading(true)
    if (!id) {
      message.error(t("Thiếu id đơn tuyển dụng để xóa."))
      setIsLoading(false)
      return false
    }
    try {
      await AdminHttpClient.delete(`${API_PREFIX_CAREER_FORMS_PATH}/${id}`)
      return true
    } catch (error) {
      message.error(t("Lỗi khi xóa đơn tuyển dụng"))
      return false
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    getCareerForms,
    getCareerFormById,
    createCareerForm,
    updateCareerForm,
    deleteCareerForm,
  }
}

export default careerFormService